import * as React from 'react';
import { useDispatch } from 'react-redux';
import { TThunkDispatch } from '../../types/redux';
import { useDidMount } from '@cian/valuation-utils-component';

import { useCountDown, usePhoneValue, useSendVerificationCode, useVerificationStep, useVerifyValue } from './utils';
import { sendFormAnalytics } from '../../actions/analytics';
import { EPhoneVerificationStep } from './constants';
import { PhoneFieldWithSubmit, VerifyField } from '../../components/PhoneVerification';
import { IBaseQuestionProps } from '../FormContainer/types/internal';
import { QuestionLayoutContainer } from '../QuestionLayoutContainer';
import { EAnswerField, EQuestionCode } from '../../types/form';
import { useAgreementCheckbox } from './utils/useAgreementCheckbox';

/**
 * Контейнер для шага,
 *  * Если type phone, то сначала будет ввод телефона, затем его подтверждение
 *  * Если type phoneVerify, то сначала будет отправлена смс с кодом подтверждения,
 *    затем экран подтверждения + возможность изменить номер телефона
 * Шаг может быть последним
 * */
export const PhoneVerificationContainer: React.FC<React.PropsWithChildren<IBaseQuestionProps>> = props => {
  const {
    question: { code },
  } = props;
  const { value: phone, onChange: onChangePhone, isValid: isPhoneValid, onSubmit } = usePhoneValue(props);
  const { seconds: countDown, start: startCountDown } = useCountDown();
  const { agreementCheckbox, toggleAgreementCheckbox } = useAgreementCheckbox();
  const { value: verificationCode, onChange: onChangeVerify, isValid: isVerifyValid } = useVerifyValue('');
  const { gotToPhoneStep, goToVerificationStep, step, title } = useVerificationStep({
    initialStep: code === EQuestionCode.PhoneVerify ? EPhoneVerificationStep.VERIFY : EPhoneVerificationStep.PHONE,
  });
  const {
    isFetching,
    error: requestError,
    sendVerificationCode,
    sendVerificationSms,
  } = useSendVerificationCode({
    verificationCode,
    phone,
    startCountDown,
    onVerifySuccess: onSubmit,
    onSendSmsSuccess: goToVerificationStep,
  });
  const dispatch = useDispatch<TThunkDispatch>();

  /**
   * Если шаг отдельного подтверждения телефона,
   * то вначале отправим смс с кодом на указанный в каком-то из прошлых шагов телефон
   * */
  useDidMount(() => {
    if (code === EQuestionCode.PhoneVerify) {
      sendVerificationSms(phone);
    }
  });

  const handleSubmit = (phoneNumber: string) => {
    dispatch(sendFormAnalytics({ phone: phoneNumber }));
    sendVerificationSms(phoneNumber);
  };

  return (
    <QuestionLayoutContainer title={title} error={props.error} showButtonPreloader={props.showButtonPreloader}>
      {step === EPhoneVerificationStep.PHONE ? (
        <PhoneFieldWithSubmit
          error={requestError}
          fetching={isFetching}
          value={phone}
          onChange={onChangePhone}
          isValid={isPhoneValid}
          checked={agreementCheckbox}
          toggleCheckbox={toggleAgreementCheckbox}
          onSubmit={agreementCheckbox ? handleSubmit : sendVerificationSms}
          inputName={EAnswerField.Phone}
        />
      ) : (
        <VerifyField
          fetching={isFetching || props.showButtonPreloader}
          seconds={countDown}
          restart={sendVerificationSms}
          phone={phone}
          value={verificationCode}
          error={requestError}
          isValid={isVerifyValid}
          onChange={onChangeVerify}
          onSubmit={sendVerificationCode}
          onChangePhone={gotToPhoneStep}
          buttonText={props.submitButtonText || 'Подтвердить'}
        />
      )}
    </QuestionLayoutContainer>
  );
};
